import { Component } from 'react'
import type { ReactNode, ErrorInfo } from 'react'
import { RefreshCw, AlertTriangle } from 'lucide-react'
import * as Sentry from '@sentry/react'

interface Props {
  children: ReactNode
}

/** Detecta erros de chunk load (deploy invalidou asset que tab antiga referencia). */
function isChunkLoadError(error: Error): boolean {
  const msg = error?.message || ''
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    error?.name === 'ChunkLoadError'
  )
}

interface State {
  hasError: boolean
  error?: Error
  eventId?: string
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo)
    // Chunk load fail = deploy invalidou asset que tab antiga ainda referencia.
    // Não é bug — é UX de recovery. Recarrega imediatamente em vez de mostrar
    // tela de erro vermelha; user perde no máximo o estado da rota atual.
    if (isChunkLoadError(error)) {
      // Marker pra evitar loop infinito caso o reload em si também falhe
      const RELOADED_KEY = 'runna_chunk_reload_attempt'
      const recentReload = sessionStorage.getItem(RELOADED_KEY)
      if (!recentReload) {
        sessionStorage.setItem(RELOADED_KEY, String(Date.now()))
        window.location.reload()
        return
      }
      // Se já tentou reload antes, deixa cair na UI normal (algo mais sério)
    } else {
      sessionStorage.removeItem('runna_chunk_reload_attempt')
    }
    const eventId = Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
      tags: { boundary: 'root' },
    })
    this.setState({ eventId })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-bg flex items-center justify-center p-8">
          <div className="max-w-md w-full text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-accent-red/10 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-accent-red" />
            </div>
            <h1 className="font-heading text-2xl font-extrabold text-bright mb-2">
              Algo deu errado
            </h1>
            <p className="text-text mb-6">
              Ocorreu um erro inesperado. Tente recarregar a pagina.
            </p>
            {this.state.error && (
              <p className="text-xs text-dim bg-card border border-card-border rounded-lg p-3 mb-6 text-left font-mono break-all">
                {this.state.error.message}
              </p>
            )}
            {this.state.eventId && (
              <p className="text-xs text-muted mb-4">
                ID do erro (envie para suporte): <span className="font-mono">{this.state.eventId.slice(0, 8)}</span>
              </p>
            )}
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Recarregar Página
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
